import { gql } from '@apollo/client'
import classNames from 'classnames'
import { useContext } from 'react'

import { getCardsByFrequency } from '../../utils'
import { BoxLockContext } from '../context'
import { PaywallLock } from '../paywall-lock'
import {
  DynamicMultipleOffersSwitchFrequencyLock_OfferLockDataFragment,
  DynamicMultipleOffersSwitchFrequencyLock_ViewerFragment,
} from './__generated__/dynamic-multiple-offers-switch-frequency-lock.graphql'
import { DynamicMultipleOffersSwitchFrequencyLockContent, ModalLock } from './components'
import styles from './dynamic-multiple-offers-switch-frequency-lock.module.scss'

export interface DynamicMultipleOffersSwitchFrequencyLockProps {
  offerLockData: DynamicMultipleOffersSwitchFrequencyLock_OfferLockDataFragment
  viewer: DynamicMultipleOffersSwitchFrequencyLock_ViewerFragment | null
}

export const DynamicMultipleOffersSwitchFrequencyLock = ({
  offerLockData,
  viewer,
}: DynamicMultipleOffersSwitchFrequencyLockProps) => {
  const { componentType } = useContext(BoxLockContext)
  const isPaywall = componentType === 'paywall'

  const { monthlyCards, yearlyCards } = getCardsByFrequency(offerLockData?.body?.cards)

  const content = (
    <DynamicMultipleOffersSwitchFrequencyLockContent
      offerLockData={offerLockData}
      viewer={viewer}
      isPaywall={isPaywall}
    />
  )

  const isOneOfferByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 1
  const isThreeOffersByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 3
  const modalContentClassName = classNames({
    [styles.modalContent]: isThreeOffersByFrequency,
  })

  switch (componentType) {
    case 'paywall':
      return <PaywallLock offerLockData={offerLockData}>{content}</PaywallLock>
    case 'modal':
      return (
        <ModalLock
          offerLockData={offerLockData}
          size={isOneOfferByFrequency ? 'sm' : 'lg'}
          modalContentClassName={modalContentClassName}
        >
          {content}
        </ModalLock>
      )
    default:
      return null
  }
}

DynamicMultipleOffersSwitchFrequencyLock.fragments = {
  offerLockData: gql`
    fragment DynamicMultipleOffersSwitchFrequencyLock_offerLockData on OfferLockData {
      ...PaywallLock_offerLockData
      ...ModalLock_offerLockData
      ...DynamicMultipleOffersSwitchFrequencyLockContent_offerLockData
    }
    ${PaywallLock.fragments.offerLockData}
    ${ModalLock.fragments.offerLockData}
    ${DynamicMultipleOffersSwitchFrequencyLockContent.fragments.offerLockData}
  `,
  viewer: gql`
    fragment DynamicMultipleOffersSwitchFrequencyLock_viewer on Profile {
      ...DynamicMultipleOffersSwitchFrequencyLockContent_viewer
    }
    ${DynamicMultipleOffersSwitchFrequencyLockContent.fragments.viewer}
  `,
}
